<template>
  <div
    :class="[$style.cardWrapper, isDragging && $style.dragging]"
    :data-testid="`stage-card-${stage.id}`"
  >
    <div :class="$style.card">
      <!-- Drag handle on the left -->
      <button
        type="button"
        :class="$style.dragHandle"
        draggable="true"
        :aria-label="`Drag ${stage.name} to reorder`"
        :data-testid="`stage-drag-${stage.id}`"
        @dragstart="handleDragStart"
        @dragend="handleDragEnd"
      >
        <DragIcon :class="$style.dragIcon" />
      </button>

      <!-- Main content area -->
      <div :class="$style.mainContent">
        <div :class="$style.content">
          <span :class="$style.stageTitle"
            >Stage {{ stage.number }}: {{ stage.name }}</span
          >

          <div :class="$style.details">
            <div :class="$style.field">
              <span :class="$style.fieldLabel">Interviewers:</span>
              <span
                v-if="hasInterviewers"
                :class="$style.fieldValue"
              >
                {{ interviewerNames }}
              </span>
              <span
                v-else
                :class="$style.missingValue"
                >Missing</span
              >
            </div>

            <div :class="$style.field">
              <span :class="$style.fieldLabel">Cost:</span>
              <span :class="$style.fieldValue"
                >${{ formatNumber(stage.cost) }}</span
              >
            </div>

            <label
              v-if="!isFinalStage"
              :class="$style.sameDayControl"
            >
              <input
                type="checkbox"
                :checked="stage.daysToNext === 0"
                :data-testid="`stage-same-day-${stage.id}`"
                @change="handleSameDayChange"
              />
              <span>Same day as next stage</span>
            </label>
          </div>

          <div
            v-if="stage.warnings && stage.warnings.length > 0"
            :class="$style.warningBadge"
          >
            <span>Warnings Identified</span>
          </div>
        </div>

        <!-- Actions row at the bottom -->
        <div :class="$style.actionsRow">
          <div :class="$style.actionsGroup">
            <button
              type="button"
              :class="$style.moveButton"
              :disabled="!canMoveEarlier"
              :aria-label="`Move ${stage.name} earlier`"
              @click="$emit('moveEarlier')"
            >
              Earlier
            </button>
            <button
              type="button"
              :class="$style.moveButton"
              :disabled="!canMoveLater"
              :aria-label="`Move ${stage.name} later`"
              @click="$emit('moveLater')"
            >
              Later
            </button>
            <button
              type="button"
              :class="$style.actionLink"
              :aria-label="`Edit ${stage.name}`"
              @click="$emit('edit')"
            >
              <EditIcon :class="$style.actionIcon" />
            </button>
            <button
              type="button"
              :class="$style.actionLink"
              :aria-label="`Delete ${stage.name}`"
              @click="$emit('delete')"
            >
              <TrashIcon :class="$style.actionIcon" />
            </button>
          </div>
          <div :class="$style.actionsDivider"></div>
          <button
            type="button"
            :class="$style.addButton"
            :aria-label="`Add stage after ${stage.name}`"
            @click="$emit('addAfter')"
          >
            <PlusIcon :class="$style.plusIcon" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import EditIcon from '~/assets/svg/edit.svg'
  import TrashIcon from '~/assets/svg/delete.svg'
  import PlusIcon from '~/assets/svg/add.svg'
  import DragIcon from '~/assets/svg/drag.svg'

  interface Interviewer {
    name: string
    linkedIn?: string
    focusArea: string
    customFocusArea?: string
    trainedByYogen: boolean
    isBackup?: boolean
  }

  interface Stage {
    id: string
    number: number
    name: string
    goal: string
    type: 'phone' | 'video' | 'onsite'
    duration: number
    useAiTools: boolean
    interviewers: Interviewer[]
    daysToNext: number
    cost: number
    warnings: string[]
  }

  const props = defineProps<{
    stage: Stage
    canMoveEarlier: boolean
    canMoveLater: boolean
    isFinalStage: boolean
  }>()

  const emit = defineEmits<{
    edit: []
    delete: []
    addAfter: []
    moveEarlier: []
    moveLater: []
    sameDayChange: [sameDay: boolean]
    dragStart: [stageId: string]
    dragEnd: []
  }>()

  const isDragging = ref(false)

  const hasInterviewers = computed(() => {
    return props.stage.interviewers && props.stage.interviewers.length > 0
  })

  const interviewerNames = computed(() => {
    if (!props.stage.interviewers) return ''
    return props.stage.interviewers
      .filter((i) => !i.isBackup)
      .map((i) => i.name)
      .join(', ')
  })

  function formatNumber(num: number): string {
    return num.toLocaleString()
  }

  function handleDragStart(e: DragEvent) {
    isDragging.value = true
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', props.stage.id)
    }
    emit('dragStart', props.stage.id)
  }

  function handleDragEnd() {
    isDragging.value = false
    emit('dragEnd')
  }

  function handleSameDayChange(event: Event) {
    emit('sameDayChange', (event.target as HTMLInputElement).checked)
  }
</script>

<style module>
  .cardWrapper {
    position: relative;
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .cardWrapper.dragging {
    opacity: 0.5;
    transform: scale(0.98);
  }

  .card {
    width: 292px;
    background: var(--color-surface-default);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    padding: var(--space-16) var(--space-16) var(--space-20) 0;
    box-shadow: var(--elevation-md);
    display: flex;
    position: relative;
  }

  /* Drag handle on the left - vertical */
  .dragHandle {
    display: flex;
    align-items: center; /* Align to top of content */
    justify-content: center;
    width: 24px;
    margin-right: var(--space-12);
    cursor: grab;
    user-select: none;
    padding: 0;
    background: transparent;
    border: none;
  }

  .dragHandle:active {
    cursor: grabbing;
  }

  /* Vertical drag icon */
  .dragIcon {
    width: 20px;
    height: 30px;
    fill: var(--color-border-default);
    transform: rotate(90deg); /* Make it vertical */
  }

  /* Main content area that stacks content and actions */
  .mainContent {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: var(--space-16); /* Space between content and actions */
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .stageTitle {
    font: var(--type-body-large);
    letter-spacing: var(--type-body-large-tracking);
    color: var(--color-text-secondary);
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
  }

  .sameDayControl {
    display: inline-flex;
    align-items: center;
    gap: var(--space-8);
    width: fit-content;
    color: var(--color-text-secondary);
    font: var(--type-label-small);
    letter-spacing: var(--type-label-small-tracking);
    cursor: pointer;
  }

  .sameDayControl input {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: var(--color-interactive-primary);
  }

  .field {
    display: flex;
    align-items: baseline;
    gap: var(--space-4);
    font: var(--type-body-large-emphasized);
    letter-spacing: var(--type-body-large-emphasized-tracking);
    color: var(--color-text-primary);
  }

  .fieldLabel {
    color: var(--color-text-primary);

    font: var(--type-label-large-emphasized);
    letter-spacing: var(--type-label-large-emphasized-tracking);
  }

  .fieldValue {
    color: var(--color-text-primary);
  }

  .missingValue {
    color: var(--color-status-error);

    font: var(--type-body-medium-emphasized);
    letter-spacing: var(--type-body-medium-emphasized-tracking);
  }

  .warningBadge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--color-status-warning-subtle);
    color: var(--color-status-warning);
    padding: var(--space-4) var(--space-12);
    border-radius: var(--radius-full);
    font: var(--type-label-large-emphasized);
    letter-spacing: var(--type-label-large-emphasized-tracking);
    margin-top: var(--space-12);
    width: fit-content;
    white-space: nowrap;
  }

  .actionsRow {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-16);
  }

  .actionsGroup {
    display: flex;
    align-items: center;
    gap: var(--space-4);
  }

  .moveButton {
    background: transparent;
    border: none;
    padding: var(--space-4);
    color: var(--color-interactive-link);
    font: var(--type-label-small-emphasized);
    letter-spacing: var(--type-label-small-emphasized-tracking);
    cursor: pointer;
  }

  .moveButton:hover:not(:disabled) {
    text-decoration: underline;
  }

  .moveButton:disabled {
    color: var(--color-text-disabled);
    cursor: not-allowed;
  }

  .actionLink {
    background: transparent;
    border: none;
    padding: var(--space-4);
    cursor: pointer;
    transition: opacity 0.2s ease;
  }

  .actionLink:hover {
    opacity: 0.7;
  }

  .actionIcon {
    width: 22px;
    height: 22px;
    fill: var(--color-text-secondary);
  }

  .actionsDivider {
    width: 1px;
    height: 22px;
    background: var(--color-border-default);
  }

  .addButton {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: opacity 0.2s ease;
  }

  .addButton:hover {
    opacity: 0.7;
  }

  .plusIcon {
    width: 16px;
    height: 16px;
    fill: var(--color-text-secondary);
  }
</style>
